import React, { SFC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LocationInfo } from '../../../shared/types/location'
import { Grid, CircularProgress, Card, CardContent, Typography, ListItem, InputAdornment, TextField, IconButton, Menu, MenuItem, Badge, Checkbox } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useCalcHeightToBottomPartialList } from '../../../utils/ui'
import { NoData } from '../../../shared/components/NoData'
import { Image } from '../../../shared/components/Image'
import { Link } from 'react-router-dom'
import SearchIcon from '@material-ui/icons/Search'
import FilterListIcon from '@material-ui/icons/FilterList'
import { useSelector } from 'react-redux'

const useStyles = makeStyles((theme) => ({
    list: {
        overflowY: 'auto',
        marginTop: theme.spacing(3),
    },
    listItem: {
        margin: theme.spacing(1, 1, 2, 1),
        display: 'flex',
        '& .Mui-selected': {
            borderBottom: `3px solid ${theme.palette.primary.main}`,
            paddingBottom: theme.spacing(2) - 3,
        },
    },
    listItemButton: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'start',
        padding: theme.spacing(2),
    },
    listContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    listContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    imageWrapper: {
        flex: '1 0 auto',
        textAlign: 'right',
        marginLeft: theme.spacing(1),
    },
    image: {
        width: '80px',
        height: '80px',
    },
    search: {
        paddingLeft: theme.spacing(1),
    },
    dataInfoWrapper: {
        textAlign: 'center',
    },
    input: {
        backgroundColor: '#f5f5f5',
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        border: '1px solid #ddd',

        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5',
            borderRadius: theme.shape.borderRadius,
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none',
            },
        },
        '& .MuiFilledInput-input': {
            padding: '17px 12px 10px 0px'
        },
        '& .MuiInputLabel-root': {
            top: '-7px',
            fontSize: '0.9rem',
        },
        '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
            marginTop: '8px',
        },
    }
}))

interface LocationListProps {
    locations: LocationInfo[]
    search: string
    onSearchChange(value: string): void
    statusFilter: string[]
    onStatusFilterChange(value: string[]): void
    selectedLocationId?: string
    onLocationSelected(location: LocationInfo): void
}

// filtre zoznamu lokalit (vyhodnotenie v Locations.tsx)
const statusFilterOptions = ['offline', 'flexiCharging', 'tenantSpecific', 'hidden', 'includeInHubject']

const getStatusFilterLabel = (option: string, t: (key: string) => string) => {
    switch (option) {
        case 'offline':
            return t('locations.filterOffline')
        case 'flexiCharging':
            return t('locations.filterFlexiCharging')
        case 'tenantSpecific':
            return t('locations.filterTenantSpecific')
        case 'hidden':
            return t('locations.filterHidden')
        case 'includeInHubject':
            return t('locations.filterIncludeInHubject')
        default:
            return option
    }
}

const LocationList: SFC<LocationListProps> = ({ 
    locations, 
    search, 
    onSearchChange, 
    statusFilter,
    onStatusFilterChange,
    selectedLocationId, 
    onLocationSelected 
}) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const locationsState = useSelector((state: any) => state.locations.items)
    const listHeightFill = useCalcHeightToBottomPartialList('listWrapper')

    const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        onSearchChange(e.target.value)
    }

    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null)

    const toggleStatusFilter = (value: string) => {
        if (statusFilter.includes(value)) {
            onStatusFilterChange(statusFilter.filter((x) => x !== value))
        } else {
            onStatusFilterChange([...statusFilter, value])
        }
    }

    return (
        <>
            <Grid container>
                {/* rovnake rozlozenie ako v zozname pouzivatelov: vyhladavanie | filter | uzky loading */}
                <Grid item xs={12} className={classes.search}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                            <TextField
                                fullWidth
                                variant="filled"
                                placeholder={t('locations.search')}
                                value={search}
                                onChange={handleSearchChanged}
                                className={classes.input}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                        <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)} style={{ marginLeft: 4 }}>
                            <Badge badgeContent={statusFilter.length} color="secondary" invisible={statusFilter.length === 0}>
                                <FilterListIcon style={{ color: statusFilter.length > 0 ? '#6100ED' : undefined }} />
                            </Badge>
                        </IconButton>
                        <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={() => setFilterAnchorEl(null)}>
                            {statusFilterOptions.map((option) => (
                                <MenuItem key={option} onClick={() => toggleStatusFilter(option)}>
                                    <Checkbox
                                        checked={statusFilter.includes(option)}
                                        style={{ color: '#6100ED', padding: '4px' }}
                                    />
                                    {getStatusFilterLabel(option, t)}
                                </MenuItem>
                            ))}
                        </Menu>
                        <div style={{ width: '32px', textAlign: 'center' }}>
                            {!locationsState.executed && <CircularProgress size={24} />}
                        </div>
                    </div>
                </Grid>
                <Grid id="listWrapper" item xs={12} className={classes.list} ref={listHeightFill}>
                    {locations.map((location: LocationInfo) => (
                        <Card key={location.id} className={classes.listItem} elevation={1}>
                            <ListItem
                                button
                                selected={location.id === selectedLocationId}
                                onClick={() => onLocationSelected(location)}
                                className={classes.listItemButton}
                            >
                                <div className={classes.listContentWrapper}>
                                    <CardContent className={classes.listContent}>
                                        <Typography variant="h6">{location.name}</Typography>
                                        <Typography component="div" variant="caption">
                                            {t('locations.chargersCount', {
                                                countTotal: location.connectorCount,
                                                countWorking: location.workingConnectors,
                                                countInUse: location.availableConnectors,
                                            })}
                                        </Typography>
                                        <Typography component="div" variant="caption">
                                            <Link to="/history">
                                                {t('locations.chargesToday', {
                                                    count: location.chargingSessionsCount ?? 0,
                                                })}
                                                {location.chargingSessionsInProgressCount !== 0 && (
                                                    <span>
                                                        {t('locations.chargesInProgress', {
                                                            count: location.chargingSessionsInProgressCount ?? 0,
                                                        })}
                                                    </span>
                                                )}
                                            </Link>
                                        </Typography>
                                    </CardContent>
                                </div>
                                <div className={classes.imageWrapper}>
                                    <Image
                                        className={classes.image}
                                        imageClassName={classes.image}
                                        url={location.coverImage ? location.coverImage.url : undefined}
                                    />
                                </div>
                            </ListItem>
                        </Card>
                    ))}

                    {locationsState.executed && locations.length === 0 && (
                        <div className={classes.dataInfoWrapper}>
                            <NoData text={t('locations.noLocations')} />
                        </div>
                    )}
                </Grid>
            </Grid>
        </>
    )
}

export default LocationList